import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil, throttle } from 'rxjs/operators';
import { LS } from 'src/app/helpers/localStorage.enum';
import { LocationService } from 'src/app/services/location/location.service';
import { MapService } from 'src/app/services/map/map.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { AnnotationStoreService } from 'src/app/stores/annotation-store/annotation-store.service';

@Component({
  selector: 'app-search-list',
  templateUrl: './search-list.component.html',
  styleUrls: ['./search-list.component.sass'],
})
export class SearchListComponent implements OnInit, OnDestroy {
  isSearch = true;
  isAdmin = false;

  @ViewChild('listContainer') listContainer: ElementRef;
  @ViewChild('searchTextBox') searchTextBox: ElementRef;

  searchObj: {
    searchValue?: string;
    searchCoordinate?: string;
    searchTypes?: string[];
    currentType?: string;
    searchRes?: any[];
    search$?: Subject<string>;
    isOpen?: { [locId: string]: boolean };
    isSearchOpen?: boolean;
    isSearchFocused?: boolean;
    /**
     * Explanation of logic on search behavior:
     * - When text input is focused, canSearchclose = false
     * - When mouse is hovering in listContainer, canSearchClose = false
     *
     * If either of the two scenario is not satisfied, isSearchOpen = true
     */
    canSearchClose?: boolean;
  } = {
    searchValue: '',
    searchTypes: ['name', 'description'],
    currentType: 'name',
    search$: new Subject(),
    isOpen: {},
    isSearchFocused: false,
    canSearchClose: true,
    isSearchOpen: false,
  };

  filterObj: {
    annotations?: {
      location_component?: any[];
      annotation_type?: any[];
      severity_type?: any[];
    };
    componentFilter?: string[];
    defectFilter?: string[];
    severityFilter?: string[];
    genericTypeKeyId?: {
      [key: string]: { name: string };
    };
    hasLoaded?: boolean;
  } = {
    hasLoaded: false,
  };

  projectsObj: {
    // projects?: BehaviorSubject<any[]>
    projects?: any[];
    loadedCards?: number;
  } = {
    projects: [],
    loadedCards: 0,
  };

  currLocation: any;

  getHeightInterval: NodeJS.Timeout;

  unsub$ = new Subject();
  showPinButton: boolean;

  get annotations() {
    return this.filterObj.annotations;
  }
  get componentFilter() {
    return this.filterObj.componentFilter;
  }
  get defectFilter() {
    return this.filterObj.defectFilter;
  }
  get severityFilter() {
    return this.filterObj.severityFilter;
  }
  // get genericTypeKeyId() {
  //   return this.filterObj.genericTypeKeyId
  // }

  get hasLoaded() {
    return this.filterObj.hasLoaded;
  }

  set annotations(val) {
    this.filterObj.annotations = val;
  }
  set componentFilter(val) {
    this.filterObj.componentFilter = val;
  }
  set severityFilter(val) {
    this.filterObj.severityFilter = val;
  }
  set defectFilter(val) {
    this.filterObj.defectFilter = val;
  }
  set genericTypeKeyId(val) {
    this.filterObj.genericTypeKeyId = val;
  }
  set hasLoaded(val) {
    this.filterObj.hasLoaded = val;
  }

  get projects() {
    return this.projectsObj.projects;
  }
  set projects(value) {
    const locations = [];
    const allLocations = this.locationStore.allLocations;
    for (const locId of value) {
      if (allLocations[locId]) locations.push(allLocations[locId]);
    }
    this.projectsObj.projects = locations;
  }
  get loadedCards() {
    return this.projectsObj.loadedCards;
  }
  set loadedCards(value) {
    this.projectsObj.loadedCards = value;
  }

  constructor(
    private locationService: LocationService,
    private layerStore: LayerStoreService,
    private mapService: MapService,
    private router: Router,
    private locationStore: LocationStoreService,
    private annotationStore: AnnotationStoreService
  ) {}

  ngOnInit(): void {
    this.getLocations();
    this.setupPermissions();

    this.locationStore.currLocation$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((currLoc) => {
        this.currLocation = currLoc;
        this.hasLoaded = false;
        this.annotations = {
          location_component: [],
          annotation_type: [],
          severity_type: [],
        };
      });
    this.layerStore.componentFilter$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((arr) => {
        this.componentFilter = arr;
      });
    this.layerStore.severityFilter$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((arr) => {
        this.severityFilter = arr;
      });
    this.layerStore.defectFilter$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((arr) => {
        this.defectFilter = arr;
      });

    this.annotationStore.annotations$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((res) => {
        if (res[0]) {
          this.mapService.isMapLoaded$
            .pipe(takeUntil(this.unsub$.asObservable()))
            .subscribe((loaded) => {
              if (!this.hasLoaded && loaded) {
                const currLoc =
                  this.locationStore.allLocations[this.currLocation.id];
                this.annotations = {
                  location_component: currLoc.defectComponents.filter(
                    (x) => x.count > 0
                  ),
                  annotation_type: currLoc.defectTypes.filter(
                    (x) => x.count > 0
                  ),
                  severity_type: currLoc.defectSeverities.filter(
                    (x) => x.value > 0
                  ),
                };

                this.layerStore.setComponentFilter = this.componentFilter =
                  this.annotations.location_component.map((i) => i.id);
                this.layerStore.setDefectFilter = this.defectFilter =
                  this.annotations.annotation_type.map((i) => i.id);
                this.layerStore.setSeverityFilter = this.severityFilter =
                  this.annotations.severity_type.map((i) => i.id);

                this.hasLoaded = true;
              }
            });
        }
      });

    // this.genericTypeStore.genericTypes$
    //   .pipe(takeUntil(this.unsub$.asObservable()))
    //   .subscribe((res) => {
    //     if (res[0]) {
    //       this.genericTypeKeyId = res.reduce(
    //         (acc, gt) => ((acc[gt.id] = gt), acc),
    //         []
    //       );
    //     }
    //   });

    this.locationStore.locations$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((loc) => {
        this.projects = loc;
      });

    this.searchObj.search$
      .pipe(
        takeUntil(this.unsub$.asObservable()),
        debounce((ev) => interval(500))
      )
      .subscribe((searchVal) => {
        this.getLocations();
      });
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.unsubscribe();
  }

  setupPermissions() {
    this.isAdmin = false;
    const currentUser = JSON.parse(localStorage.getItem(LS.currentUser));
    const permissions = currentUser.permissions;
    if (permissions && permissions['create:any.location']) this.isAdmin = true;
  }

  onComponentHover(isHover: boolean, evt: any) {
    if (isHover) this.searchObj.canSearchClose = false;
    else this.searchObj.canSearchClose = true;

    if (this.searchObj.canSearchClose && !this.searchObj.isSearchFocused)
      this.searchObj.isSearchOpen = false;
  }

  onTypeChange(type: string) {
    this.searchObj.currentType = type;
    this.getLocations();
  }

  onSearchChange(searchVal: string) {
    this.searchObj.searchValue = searchVal;

    // validate coordinate; 14.529601, 100.534832
    const regex = new RegExp(
      /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/
    );
    const coordinate = searchVal.replace(' ', '');
    this.showPinButton = regex.test(coordinate);
    if (this.showPinButton) {
      this.searchObj.searchCoordinate = coordinate;
    } else {
      this.mapService.setSearchCoordinate();
    }

    this.searchObj.search$.next(searchVal);
  }

  centerToCoordinate() {
    let coordinateArr = this.searchObj.searchCoordinate.split(',');
    const flippedCoor = [coordinateArr[1], coordinateArr[0]];

    this.mapService.setSearchCoordinate(flippedCoor);
  }

  onSearchFocused(isFocus: boolean, evt: any) {
    this.searchObj.isSearchFocused = isFocus;

    if (!this.searchObj.isSearchOpen && this.searchObj.isSearchFocused)
      this.searchObj.isSearchOpen = true;

    if (this.searchObj.canSearchClose && !this.searchObj.isSearchFocused)
      this.searchObj.isSearchOpen = false;
  }

  /**
   * Focuses into the search box
   */
  focusInSearchBox() {
    setTimeout(() => {
      this.searchTextBox.nativeElement.focus();
      this.searchTextBox.nativeElement.select();
      this.searchObj.isSearchOpen = true;
    }, 0);
  }

  toggleIsSearch(force?: boolean) {
    if (force !== undefined) {
      this.isSearch = force;
    } else {
      this.isSearch = !this.isSearch;
    }

    if (this.isSearch) {
      this.focusInSearchBox();
    }
  }

  /** Retrieves the location with any filtering properties */
  getLocations() {
    let params = [];
    const sObj = this.searchObj;
    if (sObj.searchValue && sObj.currentType) {
      let field = 'name';
      const searchVal = sObj.searchValue;

      switch (sObj.currentType) {
        default: {
          field = sObj.currentType;
        }
      }

      params.push({ key: `filtered[0][key]`, val: `${field}` });
      params.push({ key: `filtered[0][type]`, val: 'contains' });
      params.push({ key: `filtered[0][val]`, val: searchVal });
    }

    this.locationService
      .findAll(params?.length ? params : null)
      .toPromise()
      .then((res) => {
        const { data, total } = res;

        this.searchObj.searchRes = data.map(
          (loc) => this.locationStore.allLocations[loc.id]
        );
      });
  }

  toggleAnnotationBy(category, id) {
    this.layerStore.toggleAnnotationBy({ category, id });
  }

  toggleCard(id: string, force?: boolean) {
    if (force !== undefined) this.searchObj.isOpen[id] = force;
    else this.searchObj.isOpen[id] = !this.searchObj.isOpen[id];
  }

  center(location) {
    this.mapService.centerTo(location.coordinate);
  }

  redirect(location) {
    this.router.navigate(['/projects', location.id, 'details']);
  }

  /** Retrieve filtered report */
  genFilteredReport() {
    const filters = {};

    this.componentFilter.map((filter) => {
      filters['location_component'] = filters['location_component'] || [];

      filters['location_component'].push(filter);
    });
    this.severityFilter.map((filter) => {
      filters['severity_type'] = filters['severity_type'] || [];

      filters['severity_type'].push(filter);
    });
    this.defectFilter.map((filter) => {
      filters['annotation_type'] = filters['annotation_type'] || [];

      filters['annotation_type'].push(filter);
    });

    this.locationService.generateReport(this.currLocation.id, filters);
  }
}
